'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import { Controller, useForm } from 'react-hook-form';
import { Button } from 'components/ui/button';
import { OWNER_ROUTES } from 'utils/routes';
import { useFormErrorHandling } from 'hooks/useFormErrorHandling';
import { useSignupMutation } from 'generated/hooks';
import { CommunityAppEnum } from 'generated/types';
import { LoaderCircle } from 'lucide-react';
import { Input } from 'components/ui/input';
import { getOneClickLandingLinkRedirect } from 'utils/oneClickLanding';
import { useAuthTracking } from 'app/_tracking/events';

type RegisterFormProps = {
  inviteHash?: string | null;
  subdomain: string;
};

type RegisterInputs = {
  email: string;
  password: string;
  communityName: string;
  communitySubdomain?: string;
  app: CommunityAppEnum;
  inviteHash?: string;
};

export const RegisterForm = (props: RegisterFormProps) => {
  const router = useRouter();
  const { trackAuth } = useAuthTracking();
  const {
    handleSubmit,
    setError,
    formState: { errors },
    control
  } = useForm<RegisterInputs>();
  const { handleErrors } = useFormErrorHandling();
  const [registerState, registerMutation] = useSignupMutation();

  const onSubmit = async (inputs: RegisterInputs) => {
    trackAuth('email_register', 'started');
    const vars = {
      email: inputs.email,
      password: inputs.password,
      inviteHash: props.inviteHash ?? '',
    };
    const resp = await registerMutation(vars);

    if (resp.error) {
      return handleErrors<RegisterInputs>(resp.error, setError);
    }
    trackAuth('email_register', 'succeed');

    const oneClickLandingRedirect = getOneClickLandingLinkRedirect();
    if (oneClickLandingRedirect) {
      return router.push(oneClickLandingRedirect);
    }

    if (resp.data?.signup?.user?.id) {
      return router.push(OWNER_ROUTES.UP.getPath());
    }
  };

  return (
    <div className={'w-full'}>
      <form className="flex flex-col gap-5" onSubmit={handleSubmit(onSubmit)}>
        <Controller
          control={control}
          name="email"
          render={({ field, formState: { errors } }) => (
            <Input
              label="Email"
              placeholder="Enter your email"
              errorText={errors.email?.message as string}
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="password"
          render={({ field }) => (
            <Input
              label="Password"
              type="password"
              placeholder="••••••••"
              errorText={errors.password?.message as string}
              {...field}
            />
          )}
        />
        {!!errors.root && (
          <p
            className={`text-small text-error-600`}>{errors.root.message}</p>
        )}
        <Button
          disabled={registerState.fetching}
          type="submit"
          variant="primaryBrand"
          size="large"
        >
          {registerState.fetching && <LoaderCircle className="animate-spin" />}
          Sign up
        </Button>
      </form>
    </div>
  );
};
